import React, { useRef, useState } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { toastr } from "react-redux-toastr";
import { uploadCreative, saveCreative, deleteCreative } from "../../actions/ads";
import { Toggle } from "../settings/AlertCard";
import { formatCount } from "../dashboard/incomeLabels";
import {
	DEFAULT_IMAGE_SEC,
	MAX_MEDIA_MB,
	MEDIA_ACCEPT,
	durationText,
	isMediaName,
	isVideoName,
	mbText,
	shapeNote,
} from "./adLabels";

// The videos and images of one campaign, on its card. One creative plays both in
// the corner of every kiosk page (480×270) and full screen while nobody uses the
// kiosk, muted. A file is never replaced: a new one is added and the old one turned
// off, or removed while it has not played.

// What the browser can read of a file before it is sent: its size in pixels, and a
// video's length. The backend keeps them to show here; nothing depends on them.
const measure = (file) =>
	new Promise((resolve) => {
		const url = URL.createObjectURL(file);
		let settled = false;
		const done = (value) => {
			if (settled) return;
			settled = true;
			URL.revokeObjectURL(url);
			resolve(value);
		};
		// A file the browser cannot open still goes; the backend checks it
		setTimeout(() => done({}), 5000);
		if (isVideoName(file.name)) {
			const video = document.createElement("video");
			video.preload = "metadata";
			video.muted = true;
			video.onloadedmetadata = () =>
				done({ width: video.videoWidth, height: video.videoHeight, lengthSec: video.duration });
			video.onerror = () => done({});
			video.src = url;
		} else {
			const image = new Image();
			image.onload = () => done({ width: image.naturalWidth, height: image.naturalHeight });
			image.onerror = () => done({});
			image.src = url;
		}
	});

const iconButton =
	"inline-flex items-center justify-center w-8 h-8 text-gray-400 rounded-lg hover:text-red-600 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-200";

const Preview = ({ creative }) =>
	creative.type === "video" ? (
		<video
			src={creative.url}
			muted
			loop
			playsInline
			preload="metadata"
			onMouseEnter={(e) => e.target.play().catch(() => {})}
			onMouseLeave={(e) => e.target.pause()}
			className="object-contain w-full h-full"
		/>
	) : (
		<img src={creative.url} alt={creative.name} loading="lazy" className="object-contain w-full h-full" />
	);

const CreativeTile = ({ campaignId, creative, onChange, saveCreative, deleteCreative }) => {
	const [busy, setBusy] = useState(false);
	const [seconds, setSeconds] = useState(String(creative.durationSec || DEFAULT_IMAGE_SEC));
	const note = shapeNote(creative);
	const played = creative.plays > 0;

	const save = async (body) => {
		setBusy(true);
		const saved = await saveCreative(campaignId, creative._id, body);
		setBusy(false);
		if (saved) onChange(saved);
		return saved;
	};

	const saveSeconds = async () => {
		const value = Number(seconds);
		if (value === creative.durationSec) return;
		if (!Number.isInteger(value) || value < 3 || value > 300) {
			toastr.error("Зургийг 3-300 секунд харуулна");
			setSeconds(String(creative.durationSec));
			return;
		}
		if (!(await save({ enabled: creative.enabled, durationSec: value }))) setSeconds(String(creative.durationSec));
	};

	const remove = async () => {
		if (!window.confirm(`"${creative.name}"-г устгах уу?`)) return;
		setBusy(true);
		const saved = await deleteCreative(campaignId, creative._id);
		setBusy(false);
		if (saved) onChange(saved);
	};

	return (
		<div
			className={
				"overflow-hidden border rounded-lg dark:border-gray-700 " +
				(creative.enabled ? "border-gray-200" : "border-dashed border-gray-300 opacity-70")
			}
		>
			<a
				href={creative.url}
				target="_blank"
				rel="noopener noreferrer"
				className="relative block bg-gray-900"
				style={{ aspectRatio: "16 / 9" }}
				title="Шинэ цонхонд нээх"
			>
				<Preview creative={creative} />
				<span className="absolute px-1.5 py-0.5 text-xs text-white bg-black bg-opacity-60 rounded top-1 left-1">
					<i className={"mr-1 fas " + (creative.type === "video" ? "fa-film" : "fa-image")} />
					{creative.type === "video"
						? creative.lengthSec
							? `${Math.round(creative.lengthSec)} сек`
							: "Видео"
						: `${creative.durationSec} сек`}
				</span>
			</a>
			<div className="p-2 space-y-1.5">
				<div className="flex items-center justify-between gap-2">
					<p className="min-w-0 text-xs font-medium text-gray-700 truncate dark:text-gray-200" title={creative.name}>
						{creative.name || "Нэргүй"}
					</p>
					<Toggle
						checked={creative.enabled}
						onChange={(enabled) => !busy && save({ enabled, durationSec: creative.durationSec })}
						label={creative.enabled ? "Унтраах" : "Асаах"}
					/>
				</div>
				<p className="text-xs text-gray-500 dark:text-gray-400">
					{creative.width && creative.height ? `${creative.width}×${creative.height} · ` : ""}
					{mbText(creative.bytes)}
					{played && ` · ${formatCount(creative.plays)} тоглолт`}
				</p>
				{note && (
					<p className="text-xs text-yellow-700 dark:text-yellow-300">
						<i className="mr-1 fas fa-exclamation-triangle" />
						{note}
					</p>
				)}
				<div className="flex items-center justify-between gap-2">
					{creative.type === "image" ? (
						<label className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
							<input
								type="number"
								min={3}
								max={300}
								value={seconds}
								onChange={(e) => setSeconds(e.target.value)}
								onBlur={saveSeconds}
								onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
								disabled={busy}
								className="w-16 h-7 px-2 text-xs text-gray-700 bg-white border border-gray-300 rounded-md dark:text-gray-300 dark:bg-gray-700 dark:border-gray-600"
							/>
							сек харуулна
						</label>
					) : (
						<span className="text-xs text-gray-400">Өөрийн уртаараа тоглоно</span>
					)}
					<button
						type="button"
						onClick={remove}
						disabled={busy || played}
						className={iconButton + (busy || played ? " opacity-40 cursor-not-allowed" : "")}
						title={played ? "Тоглолт бүртгэгдсэн тул тайланд үлдэнэ. Унтрааж болно." : "Устгах"}
						aria-label="Устгах"
					>
						<i className="fas fa-trash-alt" />
					</button>
				</div>
			</div>
		</div>
	);
};

const Creatives = ({ campaign, onChange, uploadCreative, saveCreative, deleteCreative }) => {
	const input = useRef(null);
	// null, or how much of the upload has gone, 0-1
	const [progress, setProgress] = useState(null);

	const pick = async (e) => {
		const file = e.target.files[0];
		e.target.value = "";
		if (!file) return;
		if (!isMediaName(file.name)) {
			toastr.error("MP4, WebM видео эсвэл JPG, PNG, WebP зураг оруулна уу");
			return;
		}
		if (file.size > MAX_MEDIA_MB * 1024 * 1024) {
			toastr.error(`Файл ${MAX_MEDIA_MB} MB-аас ихгүй байна. Видеог 1080p, 30 секундээс богино болгож шахна уу.`);
			return;
		}
		setProgress(0);
		const fields = await measure(file);
		const saved = await uploadCreative(campaign._id, file, fields, setProgress);
		setProgress(null);
		if (saved) onChange(saved);
	};

	return (
		<div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
			{campaign.creatives.map((creative) => (
				<CreativeTile
					key={creative._id}
					campaignId={campaign._id}
					creative={creative}
					onChange={onChange}
					saveCreative={saveCreative}
					deleteCreative={deleteCreative}
				/>
			))}
			<button
				type="button"
				onClick={() => input.current.click()}
				disabled={progress !== null}
				className="flex flex-col items-center justify-center gap-1 p-4 text-sm text-gray-500 border-2 border-gray-300 border-dashed rounded-lg hover:border-purple-400 hover:text-purple-600 dark:border-gray-600 dark:text-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-200"
				style={{ minHeight: "9rem" }}
			>
				{progress === null ? (
					<>
						<i className="text-lg fas fa-plus" />
						<span className="font-medium">Бүтээл нэмэх</span>
						<span className="text-xs text-center">
							MP4 видео эсвэл зураг, {MAX_MEDIA_MB} MB хүртэл
							<br />
							1920×1080 (16:9), дуугүй
						</span>
					</>
				) : (
					<>
						<i className="text-lg fas fa-spinner fa-spin" />
						<span className="font-medium">{progress < 1 ? `${Math.round(progress * 100)}%` : "Хадгалж байна..."}</span>
					</>
				)}
			</button>
			<input ref={input} type="file" accept={MEDIA_ACCEPT} onChange={pick} className="hidden" />
		</div>
	);
};

Preview.propTypes = {
	creative: PropTypes.object.isRequired,
};

CreativeTile.propTypes = {
	campaignId: PropTypes.string.isRequired,
	creative: PropTypes.object.isRequired,
	onChange: PropTypes.func.isRequired,
	saveCreative: PropTypes.func.isRequired,
	deleteCreative: PropTypes.func.isRequired,
};

Creatives.propTypes = {
	campaign: PropTypes.object.isRequired,
	onChange: PropTypes.func.isRequired,
	uploadCreative: PropTypes.func.isRequired,
	saveCreative: PropTypes.func.isRequired,
	deleteCreative: PropTypes.func.isRequired,
};

export default connect(null, { uploadCreative, saveCreative, deleteCreative })(Creatives);
